/**
 * AdvertiserApiV2Page — admin surface for `advertiser-api-v2`.
 *
 * Layout:
 *   1. Header (green accent, light theme).
 *   2. Advertiser selector — scoped advertiser_id (required by the BE).
 *   3. Issue-key panel — name + scopes (conversions.write / reports.read /
 *      invoices.read). On success, one-time raw-key reveal.
 *   4. Quickstart snippets — POST /conversions + GET /reports/campaigns.
 *   5. Active keys table — preview, scopes, created, revoke.
 *   6. Access log table — recent partner requests with status code.
 *
 * Green-only palette. Light theme. Plain Tailwind. No emerald/sky/blue/orange/
 * purple. No dark mode. No emojis. No competitor names.
 */
import { useState, type FormEvent } from "react";
import {
	toggleScope,
	V2_SCOPES,
	V2_SCOPE_DESCRIPTIONS,
	type AdvertiserV2Scope,
} from "./api";
import {
	useAdvertiserApiV2AccessLog,
	useAdvertiserApiV2Create,
	useAdvertiserApiV2Delete,
	useAdvertiserApiV2List,
} from "./hooks";

// --- Tailwind tokens (green family, light theme) ---------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"text-3xl font-semibold text-green-700";
const SECTION_LABEL =
	"text-xs font-semibold uppercase tracking-wider text-zinc-500";
const MONO_BOX =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono text-zinc-800 whitespace-pre-wrap break-all";

// Inline SVG key icon (no emojis, no external deps).
function KeyIcon({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<circle cx="7.5" cy="15.5" r="3.5" />
			<path d="M10 13l8-8" />
			<path d="M15 5h3v3" />
			<path d="M13.5 7.5l2 2" />
		</svg>
	);
}

// --- Component -------------------------------------------------------------

export function AdvertiserApiV2Page() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [advertiserIdApplied, setAdvertiserIdApplied] = useState("");
	const [name, setName] = useState("");
	const [scopes, setScopes] = useState<AdvertiserV2Scope[]>([
		"conversions.write",
	]);
	const [revealed, setRevealed] = useState<string | null>(null);
	const [revealName, setRevealName] = useState<string | null>(null);

	const listQuery = useAdvertiserApiV2List(advertiserIdApplied);
	const createMut = useAdvertiserApiV2Create();
	const deleteMut = useAdvertiserApiV2Delete();
	const accessLogQuery = useAdvertiserApiV2AccessLog(advertiserIdApplied, 50);

	const handleApplyAdvertiser = (e: FormEvent) => {
		e.preventDefault();
		setAdvertiserIdApplied(advertiserId.trim());
	};

	const handleIssue = (e: FormEvent) => {
		e.preventDefault();
		const advId = advertiserIdApplied.trim() || advertiserId.trim();
		if (!advId || !name.trim() || scopes.length === 0) return;
		createMut.mutate(
			{ advertiser_id: advId, name: name.trim(), scopes },
			{
				onSuccess: (resp) => {
					setRevealed(resp.raw_key);
					setRevealName(resp.key.name);
					setName("");
					setScopes(["conversions.write"]);
					setAdvertiserIdApplied(advId);
				},
			},
		);
	};

	const keys = listQuery.data?.keys ?? [];
	const activeKeys = keys.filter((k) => k.active);
	const accessLogEntries = accessLogQuery.data?.entries ?? [];
	const hasAdvertiser = advertiserIdApplied.trim().length > 0;

	return (
		<main
			data-testid="advertiser-api-v2-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			{/* 1. Header ---------------------------------------------------- */}
			<header className="flex flex-col gap-2">
				<p className={SECTION_LABEL}>advertiser-api-v2</p>
				<h1 className={HEADLINE}>Advertiser API v2 keys</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Mint per-advertiser keys with scoped access to push
					conversions, pull campaign and publisher reports, and list
					invoices. Each key is SHA-256 hashed at rest and revealed
					exactly once on issue.
				</p>
			</header>

			{/* 2. Advertiser selector --------------------------------------- */}
			<section className={CARD} aria-label="Select advertiser">
				<h2 className="text-base font-semibold text-zinc-900">
					Advertiser scope
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Enter the advertiser identifier to load keys and access log.
				</p>
				<form
					onSubmit={handleApplyAdvertiser}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="advertiser-api-v2-scope-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="advertiser_id (e.g. adv_acme)"
						value={advertiserId}
						onChange={(e) => setAdvertiserId(e.currentTarget.value)}
						maxLength={64}
						data-testid="advertiser-api-v2-advertiser-input"
					/>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={!advertiserId.trim()}
						data-testid="advertiser-api-v2-scope-apply"
					>
						Apply
					</button>
				</form>
				{hasAdvertiser ? (
					<p
						className="mt-2 text-xs text-zinc-500"
						data-testid="advertiser-api-v2-scope-applied"
					>
						Scoped to <code className="font-mono">{advertiserIdApplied}</code>
					</p>
				) : null}
			</section>

			{/* 3. Issue panel ----------------------------------------------- */}
			<section className={CARD} aria-label="Issue a new advertiser-api-v2 key">
				<div className="flex items-center justify-between">
					<h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900">
						<KeyIcon className="h-4 w-4 text-green-700" />
						Issue a new key
					</h2>
					<span className="text-xs text-zinc-500">
						Raw key shown once. Store it immediately.
					</span>
				</div>

				<form
					onSubmit={handleIssue}
					className="mt-4 flex flex-col gap-4"
					data-testid="advertiser-api-v2-issue-form"
				>
					<label className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="font-medium">Key name</span>
						<input
							type="text"
							className={INPUT}
							placeholder="e.g. Production server"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="advertiser-api-v2-name-input"
							maxLength={128}
						/>
					</label>

					{/* Scopes */}
					<fieldset
						className="flex flex-col gap-2"
						data-testid="advertiser-api-v2-scopes"
					>
						<legend className="text-sm font-medium text-zinc-700">
							Scopes
						</legend>
						<div className="overflow-hidden rounded-[6px] border border-zinc-200">
							<table className="min-w-full divide-y divide-zinc-200 text-sm">
								<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
									<tr>
										<th className="w-12 px-3 py-2"> </th>
										<th className="px-3 py-2">Scope</th>
										<th className="px-3 py-2">Description</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-100 bg-white">
									{V2_SCOPES.map((s) => {
										const checked = scopes.includes(s);
										const toggleId = `advertiser-api-v2-scope-${s}`;
										return (
											<tr key={s}>
												<td className="px-3 py-2 align-top">
													<input
														type="checkbox"
														id={toggleId}
														checked={checked}
														onChange={() =>
															setScopes((prev) => toggleScope(prev, s))
														}
														disabled={createMut.isPending}
														className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600"
														data-testid={`advertiser-api-v2-scope-toggle-${s}`}
													/>
												</td>
												<td className="px-3 py-2 align-top">
													<label
														htmlFor={toggleId}
														className="font-mono text-xs text-zinc-800"
													>
														{s}
													</label>
												</td>
												<td className="px-3 py-2 align-top text-xs text-zinc-600">
													{V2_SCOPE_DESCRIPTIONS[s]}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						{scopes.length === 0 ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="advertiser-api-v2-scope-error"
							>
								Select at least one scope.
							</p>
						) : null}
					</fieldset>

					<div className="flex items-center justify-between pt-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!name.trim() ||
								scopes.length === 0 ||
								!(advertiserIdApplied.trim() || advertiserId.trim())
							}
							data-testid="advertiser-api-v2-submit"
						>
							{createMut.isPending ? "Issuing." : "Issue key"}
						</button>
						{createMut.error ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="advertiser-api-v2-issue-error"
							>
								{createMut.error.message}
							</p>
						) : null}
					</div>
				</form>

				{/* One-time reveal */}
				{revealed ? (
					<div
						className="mt-5 rounded-[6px] border border-green-200 bg-green-50 p-4"
						role="status"
						data-testid="advertiser-api-v2-reveal"
					>
						<p className="text-sm font-semibold text-green-900">
							Key issued{revealName ? `: ${revealName}` : ""}
						</p>
						<p className="mt-1 text-xs text-green-800">
							Copy the raw key now. You will not see it again.
						</p>
						<div className={`${MONO_BOX} mt-3`}>{revealed}</div>
						<div className="mt-2 flex items-center gap-3">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									setRevealed(null);
									setRevealName(null);
								}}
								data-testid="advertiser-api-v2-reveal-dismiss"
							>
								Dismiss
							</button>
						</div>
					</div>
				) : null}
			</section>

			{/* 4. Quickstart snippets ----------------------------------------- */}
			<section className={CARD} aria-label="Advertiser API v2 quickstart">
				<h2 className="text-base font-semibold text-zinc-900">
					Quickstart
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Advertisers authenticate with the{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs">
						x-advertiser-api-v2-key
					</code>{" "}
					header. Endpoints:
				</p>

				<div className="mt-4 grid gap-4 sm:grid-cols-2">
					<div>
						<p className={SECTION_LABEL}>
							POST /api/v1/advertiser-api-v2/conversions
						</p>
						<pre
							className={`${MONO_BOX} mt-2`}
							data-testid="advertiser-api-v2-docs-post"
						>
{`curl -X POST \\
  https://api.example.com/api/v1/advertiser-api-v2/conversions \\
  -H "x-advertiser-api-v2-key: adv2_xxxx_xxxxxxxxxxxx" \\
  -H "content-type: application/json" \\
  -d '{
    "click_id": "clk_abc123",
    "goal": "signup",
    "revenue_cents": 2499,
    "currency": "USD"
  }'`}
						</pre>
					</div>
					<div>
						<p className={SECTION_LABEL}>
							GET /api/v1/advertiser-api-v2/reports/campaigns
						</p>
						<pre
							className={`${MONO_BOX} mt-2`}
							data-testid="advertiser-api-v2-docs-get"
						>
{`curl -G \\
  https://api.example.com/api/v1/advertiser-api-v2/reports/campaigns \\
  -H "x-advertiser-api-v2-key: adv2_xxxx_xxxxxxxxxxxx" \\
  --data-urlencode "from=1714521600" \\
  --data-urlencode "to=1717113600"`}
						</pre>
					</div>
				</div>
			</section>

			{/* 5. Active keys ------------------------------------------------- */}
			<section className={CARD} aria-label="Active advertiser-api-v2 keys">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Active keys
					</h2>
					<span className="text-xs text-zinc-500">
						{activeKeys.length}{" "}
						{activeKeys.length === 1 ? "key" : "keys"}
					</span>
				</div>

				{!hasAdvertiser ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-scope-required"
					>
						Enter an advertiser identifier above to view keys.
					</p>
				) : listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="advertiser-api-v2-list-error"
					>
						{listQuery.error.message}
					</p>
				) : activeKeys.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-empty"
					>
						No active keys for this advertiser yet.
					</p>
				) : (
					<div
						className="mt-3 overflow-hidden rounded-[6px] border border-zinc-200"
						data-testid="advertiser-api-v2-list"
					>
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2">Name</th>
									<th className="px-3 py-2">Preview</th>
									<th className="px-3 py-2">Scopes</th>
									<th className="px-3 py-2">Created</th>
									<th className="px-3 py-2"> </th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{activeKeys.map((row) => (
									<tr
										key={row.id}
										data-testid={`advertiser-api-v2-row-${row.id}`}
									>
										<td className="px-3 py-2 text-zinc-900">
											{row.name}
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.key_preview}
										</td>
										<td className="px-3 py-2">
											<div className="flex flex-wrap gap-1">
												{row.scopes.map((s) => (
													<span
														key={s}
														className="rounded border border-green-200 bg-green-50 px-1.5 py-0.5 font-mono text-xs text-green-800"
													>
														{s}
													</span>
												))}
											</div>
										</td>
										<td className="px-3 py-2 text-xs text-zinc-500">
											{new Date(row.created_at * 1000).toISOString()}
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`advertiser-api-v2-revoke-${row.id}`}
											>
												Revoke
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* 6. Access log -------------------------------------------------- */}
			<section className={CARD} aria-label="Advertiser-api-v2 access log">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Access log
					</h2>
					<span className="text-xs text-zinc-500">
						Last 50 partner requests
					</span>
				</div>

				{!hasAdvertiser ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-access-log-scope-required"
					>
						Enter an advertiser identifier above to view the access log.
					</p>
				) : accessLogQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-access-log-loading"
					>
						Loading.
					</p>
				) : accessLogQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="advertiser-api-v2-access-log-error"
					>
						{accessLogQuery.error.message}
					</p>
				) : accessLogEntries.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-v2-access-log-empty"
					>
						No partner requests logged yet.
					</p>
				) : (
					<div
						className="mt-3 overflow-hidden rounded-[6px] border border-zinc-200"
						data-testid="advertiser-api-v2-access-log-list"
					>
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2">Time</th>
									<th className="px-3 py-2">Method</th>
									<th className="px-3 py-2">Path</th>
									<th className="px-3 py-2">Status</th>
									<th className="px-3 py-2">Key</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{accessLogEntries.map((row) => {
									const status = row.status_code;
									const ok = status < 400;
									const chip = ok
										? "border-green-200 bg-green-50 text-green-800"
										: "border-red-200 bg-red-50 text-red-700";
									return (
										<tr
											key={row.id}
											data-testid={`advertiser-api-v2-access-log-row-${row.id}`}
										>
											<td className="px-3 py-2 text-xs text-zinc-500">
												{new Date(row.ts * 1000).toISOString()}
											</td>
											<td className="px-3 py-2 font-mono text-xs text-zinc-800">
												{row.method}
											</td>
											<td className="px-3 py-2 font-mono text-xs text-zinc-700">
												{row.path}
											</td>
											<td className="px-3 py-2">
												<span
													className={`rounded border px-1.5 py-0.5 font-mono text-xs ${chip}`}
												>
													{status}
												</span>
											</td>
											<td className="px-3 py-2 font-mono text-xs text-zinc-500">
												{row.key_id}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AdvertiserApiV2Page;
